import React, { useState, useEffect } from 'react';
import { QuestionService, QuestionFilters } from '../../services/QuestionService';

interface FilterFormProps {
  onFilterSubmit: (filters: QuestionFilters) => void;
  initialFilters?: Partial<QuestionFilters>;
  filterOptions?: {
    levels?: { id: number; name: string }[];
    statuses?: { id: string; name: string }[];
    categories?: { id: string; name: string }[];
  };
}

const inputClass = "px-3 py-2 border border-[#ddd] rounded text-sm bg-white focus:outline-none focus:border-[#007bff] transition-colors";
const glowBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none',
  borderRadius: '10px', padding: '10px 18px', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  minWidth: '130px',
};
const resetBtn: React.CSSProperties = {
  background: 'linear-gradient(120deg, #f2f6ff, #e4ebff)', color: '#0c1a36', border: 'none',
  borderRadius: '10px', padding: '10px 18px', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 8px 18px rgba(12,28,70,0.12)', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  minWidth: '130px',
};

const FilterForm: React.FC<FilterFormProps> = ({ onFilterSubmit, initialFilters = {}, filterOptions = {} }) => {
  const [filters, setFilters] = useState<QuestionFilters>({ level: 0, status: '', category: '', question: '', ...initialFilters });
  const [localStorageKey] = useState('userFilters');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(localStorageKey);
    if (saved) {
      try { setFilters(prev => ({ ...prev, ...JSON.parse(saved) })); } catch { }
    }
  }, [localStorageKey]);

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(filters));
  }, [filters, localStorageKey]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); setError(null);
    try { onFilterSubmit(filters); } catch {
      setError('Ocorreu um erro ao processar os filtros. Por favor, tente novamente.');
    } finally { setIsLoading(false); }
  };

  const handleReset = () => {
    setFilters({ level: 0, status: '', category: '', question: '' });
    localStorage.removeItem(localStorageKey);
  };

  return (
    <div className="p-6 mb-6 bg-[#f5f5f5] rounded-lg shadow-sm">
      <h2 className="mt-0 mb-5 text-2xl text-[#111827] font-bold">Filtros</h2>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-wrap gap-4 mb-5">
          {/* Level */}
          <div className="flex flex-col gap-2 min-w-[200px] flex-1">
            <label className="text-sm text-[#111827] font-medium" htmlFor="level">Nível</label>
            <select id="level" name="level" value={filters.level} onChange={handleChange} className={inputClass}>
              <option value="">Todos</option>
              {filterOptions.levels?.map(l => <option key={l.id} value={l.id.toString()}>{l.name}</option>)}
            </select>
          </div>
          {/* Status */}
          <div className="flex flex-col gap-2 min-w-[200px] flex-1">
            <label className="text-sm text-[#111827] font-medium" htmlFor="status">Status</label>
            <select id="status" name="status" value={filters.status} onChange={handleChange} className={inputClass}>
              <option value="">Todos</option>
              {filterOptions.statuses?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {/* Category */}
          <div className="flex flex-col gap-2 min-w-[200px] flex-1">
            <label className="text-sm text-[#111827] font-medium" htmlFor="category">Categoria</label>
            <select id="category" name="category" value={filters.category} onChange={handleChange} className={inputClass}>
              <option value="">Todas</option>
              {filterOptions.categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {/* Question */}
          <div className="flex flex-col gap-2 min-w-[200px] flex-1">
            <label className="text-sm text-[#111827] font-medium" htmlFor="question">Questão</label>
            <input type="text" id="question" name="question" value={filters.question} onChange={handleChange} placeholder="Digite a questão" className={inputClass} />
          </div>
        </div>

        {error && (
          <div className="text-[#dc3545] bg-[#f8d7da] border border-[#f5c6cb] rounded px-3.5 py-2.5 mb-4 text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-4 mt-4">
          <button type="button" style={resetBtn} onClick={handleReset} disabled={isLoading} className="disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none">Limpar</button>
          <button type="submit" style={glowBtn} disabled={isLoading} className="disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none">
            {isLoading ? 'Carregando...' : 'Buscar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FilterForm;